<template>
  <v-container fluid>
    <PageHeader title="Invitations" icon="mdi-email-open-outline" />
    <v-card>
      <v-data-table
        :items-per-page="-1"
        :must-sort="true"
        :no-data-text="''"
        :headers="headers.filter(h => h.show !== false)"
        :items="invitations"
        :loading="loading"
        :search="searchQuery"
        v-model:sort-by="sortBy"
      >
        <template v-slot:item.icon="{ item }">
          <div v-if="item.expired" style="margin-top: 2px; margin-bottom: 2px; color: rgb(var(--v-theme-error))">
            <v-tooltip location="bottom" attach>
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" style="color: rgb(var(--v-theme-error))">mdi-information-outline</v-icon>
              </template>
              This invitation has expired. Please request an invitation be resent from {{ item.companyName }}.
            </v-tooltip>
            EXPIRED
          </div>
          <img :src="`${API_URL}/logo/${item.senderCompanyId}`" />
        </template>
        <template v-slot:item.name="{ item }">
          <div v-html="highlight(item.name, searchQuery)"></div>
        </template>

        <template v-slot:bottom></template>

        <template v-slot:item.buttons="{ item }">
          <div class="d-flex justify-end align-center ga-2 flex-nowrap">
            <v-btn
              size="large"
              color="primary"
              @click="additionalInvite(item)"
              variant="flat"
              :disabled="!isAdmin"
              v-if="!item.expired"
              >Accept</v-btn
            >
            <v-btn
              size="large"
              color="error"
              @click="reject(item)"
              variant="flat"
              :disabled="!isAdmin"
              v-if="!item.expired"
              >Reject</v-btn
            >
            <v-btn
              size="large"
              color="error"
              @click="deleteInvitation(item)"
              variant="flat"
              :disabled="!isAdmin"
              v-if="item.expired"
              >Delete</v-btn
            >
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import Auth0Mixin from "../Auth0Mixin";
import PageHeader from "@/components/PageHeader.vue";
import filterService from "@/services/filterService.js";
import searchUtilsService from "@/services/searchUtilsService.js";
import partyTypeList from "@/constants/partyTypeList";
import partyType from "@/constants/partyType";
import invitationService from "@/services/invitationService";
import { API_URL } from "@/env";
import { roles } from "@/services/constants";
import orgUnitService from "@/services/admin/orgUnitService";
import { mapGetters } from "vuex";
import partyService from "@/services/partyService";
import moment from "moment";

export default {
  name: "InvitationList",
  mixins: [Auth0Mixin],
  components: { PageHeader },
  props: {
    idByNotification: Number,
  },
  data() {
    return {
      headers: [
        { title: "", key: "icon" },
        { title: "Connection Name", key: "companyName" },
        { title: "Company", key: "senderOrgUnitNameOfReceiverCompany" },
        { title: "Invite To", key: "senderPartyNameOfReceiverCompany" },
        { title: "Company Address", key: "companyAddress" },
        { title: "Sender Email", key: "senderEmail" },
        { title: "Sender Tax ID", key: "maskedTaxNumber" },
        { title: "Received Date", key: "formattedReceivedDate" },
        { title: "", key: "buttons" },
      ],
      sortBy: [{ key: "formattedReceivedDate", order: "desc" }],
      invitations: [],
      parties: [],
      searchQuery: "",
      loading: true,
      highlight: filterService.highlight,
      partyTypeList,
      API_URL,
      orgUnits: [],
      recalculatePartyList: false,
      recalculateExistingParty: false,
    };
  },

  computed: {
    ...mapGetters("auth0Store", ["selectedCompanyId"]),

    searchLabel: function() {
      return searchUtilsService.getLabel("Invitations", this.invitations);
    },
    isAdmin() {
      return this.$store.getters["auth0Store/userRoles"].includes(roles.Administrator);
    },
    filteredParties() {
      return index => {
        this.recalculatePartyList;
        return this.parties
          .filter(
            x => x.partyTypeId === this.invitations[index].partyTypeId && !(x.isInviteSent && !x.connectedCompanyId)
          )
          .map(x => ({ title: x.name, value: x.number }));
      };
    },
    existingParty() {
      return index => {
        this.recalculateExistingParty;
        return !!(this.invitations[index]?.partyNumber && this.invitations[index]?.partyNumber.text);
      };
    },
  },
  methods: {
    selectPartyType(partyTypeId, item) {
      this.invitations[item.rowIndex].partyTypeId = partyTypeId;
      this.invitations[item.rowIndex].partyNumber = null;
      this.recalculatePartyList = !this.recalculatePartyList;
    },
    changePartyNumber(partyNumber, item) {
      this.invitations[item.rowIndex].partyNumber = partyNumber;
      this.recalculateExistingParty = !this.recalculateExistingParty;
    },
    async init() {
      await this.populateInvitations();
      if (this.idByNotification) {
        var inviteFromNotification = this.invitations.find(x => x.id === this.idByNotification);
        if (!inviteFromNotification) {
          let invitation = await invitationService.getInvitationById(this.idByNotification);

          let msg = "The invite has already been ";
          if (invitation == null) {
            msg += "actioned on";
          } else {
            if (invitation.accepted) {
              msg += "accepted by user ";
            } else {
              msg += "rejected by user ";
            }
            msg += invitation.updateUser;
          }

          this.$root.$snackbar.message(msg);
        }
      }
    },

    async populateInvitations() {
      this.orgUnits = (await orgUnitService.getList(true)).items;
      this.invitations = await invitationService.getPending();

      let onlyOneOrgUnit = this.orgUnits.length === 1;

      this.parties = (
        await partyService.search(true, "", -1, "", {
          sortBy: [{ key: "name", order: "asc" }],
          page: 1,
          itemsPerPage: 10000,
        })
      ).items;

      this.invitations.forEach((inv, ind) => {
        inv.partyTypeId = partyType.PURCHASER;
        inv.orgUnits = this.orgUnits;
        inv.sourceParties = this.parties;
        inv.disablePartyType =
          inv.senderPartyTypeOfReceiverCompany === partyType.VENDOR ||
          inv.senderPartyTypeOfReceiverCompany === partyType.OWNER ||
          inv.senderPartyTypeOfReceiverCompany === partyType.OPERATOR;
        inv.orgUnitIds = onlyOneOrgUnit ? [this.orgUnits[0].id] : [];
        inv.rowIndex = ind;
        inv.formattedReceivedDate = moment(inv.receivedDate).format("MM/DD/YYYY");
      });
      this.loading = false;
    },

    async reject(invitation) {
      await invitationService.reject(invitation.id);
      this.populateInvitations();
      this.$store.dispatch("invitationStore/removePendingInvitation");
      this.$root.$snackbar.message("Invitation Rejected");
    },

    async accept(invitation) {
      this.$router.push({ name: "Invitation", params: { invitationId: invitation.id } });
    },

    async additionalInvite(invitation) {
      this.$router.push({
        name: "Invitation",
        params: { invitationId: invitation.id },
        query: { additionalInvite: true },
      });
    },
    async deleteInvitation(invitation) {
      await invitationService.delete(invitation.id);
      this.populateInvitations();
      this.$store.dispatch("invitationStore/removePendingInvitation");
      this.$root.$snackbar.message("Invitation Deleted");
    },
  },
};
</script>
